import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Crumbs, WqTable, PAYMENT_METHODS } from '../../components/documentation/web-integration';

const CONTENTS = ['Overview', 'Payment methods', 'Choosing your methods', 'Transaction statuses'];

const STATUSES = [
    [
        <strong>Pending</strong>,
        'The customer has opened the checkout or started paying, but the result is not confirmed yet.',
        "Wait. Don't deliver the order yet.",
    ],
    [
        <strong>Successful</strong>,
        'The customer paid the exact amount and the bank confirmed it.',
        'Deliver the order.',
    ],
    [
        <strong>Failed</strong>,
        'The payment was declined or rejected — for example, insufficient funds or a wrong amount. The customer was not charged, or the money was reversed to them.',
        'Ask the customer to try again or use another method.',
    ],
    [
        <strong>Abandoned</strong>,
        'The customer left the checkout without paying or cancelled the transaction.',
        'Follow up with the customer if needed.',
    ],
    [
        <strong>Expired</strong>,
        'The checkout or virtual account timed out before the customer paid.',
        'Send the customer a new payment link or checkout.',
    ],
    [
        <strong>Error / Timeout</strong>,
        "WayaQuick couldn't confirm the result with the bank.",
        "Don't refund or charge again. Check later or contact us with the reference.",
    ],
    [
        <strong>Refunded</strong>,
        'You refunded a successful payment with Refund Customer.',
        'No action needed.',
    ],
];

function Payment() {
    return (
        <DocPage
            seo={{
                title: 'Payment Methods and Transaction Statuses',
                description:
                    'Payment methods on the WayaQuick checkout (dynamic virtual account, bank transfer, card, USSD and PayAttitude) and what each transaction status means.',
            }}
            eyebrow="Merchants · How payments work"
            title={
                <>
                    WayaQuick — <em>Payments</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Payments · Five ways to pay, one checkout."
            prev={{ to: '/payment-life-cycle', label: 'Payment Life Cycle' }}
            // next={{ to: '/merchant-dashboard', label: 'WayaQuick Merchant Dashboard' }}
            next={{ to: '/payment-link', label: 'WayaQuick Payment Links' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Overview</h2>
                <p className="wq-lead">
                    The WayaQuick checkout gives your customers several ways to pay, and every payment has a status
                    so you always know what happened.
                </p>
                <p className="wq-p">
                    Offering more than one method means fewer lost sales: if a customer's card is declined or their
                    bank's app is down, they can pay another way without leaving the checkout.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Payment methods</h2>
                <p className="wq-p">Customers can pay on the WayaQuick checkout with any of these methods:</p>
                <div className="wq-methods">
                    {PAYMENT_METHODS.map((method) => (
                        <div key={method.name} className="wq-method-card">
                            <div className="wq-method-card-title">{method.name}</div>
                            <div className="wq-method-card-desc">{method.description}</div>
                        </div>
                    ))}
                </div>
                <Note label="Exact amounts only">
                    Whatever the method, the customer must pay the exact amount. A payment for more or less is
                    rejected automatically, and if the money has already come through it is reversed to the customer.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Choosing your methods</h2>
                <p className="wq-p">
                    Customers only see the methods turned on for your account. Turn methods on or off in your
                    dashboard at:
                </p>
                <Crumbs items={['Settings', 'WayaQuick Settings', 'Payment Preferences']} />
                <p className="wq-p">
                    Your choice applies everywhere you take payments — payment links, invoices, your website and your
                    mobile app.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Transaction statuses</h2>
                <p className="wq-p">
                    Every payment in your dashboard's transaction list shows a status. Use it to decide when to
                    deliver an order or follow up with a customer.
                </p>
                <WqTable columns={['Status', 'What it means', 'What to do']} rows={STATUSES} />
                <p className="wq-p">
                    Disputes and chargebacks are handled separately from the payment status — see{' '}
                    <Link to="/payment-life-cycle" className="wq-link">
                        Payment Life Cycle
                    </Link>
                    . Developers can find every status the API returns in the{' '}
                    <Link to="/standard-api-integration" className="wq-link">
                        Standard API Integration
                    </Link>
                    .
                </p>
            </section>
        </DocPage>
    );
}

export default Payment;
